import type { Company } from '../services/api/companyService';

/** DB row 或 Company 物件上的 Premium 欄位（snake / camel 皆可） */
export type CompanyPremiumFieldsRow = {
  is_premium?: boolean | null;
  isPremium?: boolean | null;
  premium_until?: string | null;
  premiumUntil?: string | null;
};

export function normalizeCompanyPremiumFields(
  row: CompanyPremiumFieldsRow
): Pick<Company, 'isPremium' | 'premiumUntil'> {
  return {
    isPremium: Boolean(row.isPremium ?? row.is_premium),
    premiumUntil: row.premiumUntil ?? row.premium_until ?? undefined,
  };
}

/** 公司 Premium 是否在有效期內（含試用期）；過期當日即視為無效（方案 A） */
export function isCompanyPremiumActive(
  company: Pick<Company, 'isPremium' | 'premiumUntil'> | null | undefined
): boolean {
  if (!company?.isPremium || !company.premiumUntil) return false;
  return new Date(company.premiumUntil) > new Date();
}

/** 與 Guard 相同標準，支援 companies 表 snake_case 欄位 */
export function isCompanyPremiumActiveFromRow(
  row: CompanyPremiumFieldsRow | null | undefined
): boolean {
  if (!row) return false;
  return isCompanyPremiumActive(normalizeCompanyPremiumFields(row));
}

/** 曾有訂閱但已過期（可顯示續費提示，不可使用公司端功能） */
export function isCompanyPremiumExpired(
  company: Pick<Company, 'isPremium' | 'premiumUntil'> | null | undefined
): boolean {
  if (!company?.premiumUntil) return false;
  return new Date(company.premiumUntil) <= new Date();
}

export function getCompanyPremiumDaysLeft(
  premiumUntil: string | undefined
): number | null {
  if (!premiumUntil) return null;
  return Math.ceil(
    (new Date(premiumUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );
}

export const COMPANY_STAFF_USER_TYPES = ['administrative_staff', 'finance_staff'] as const;

export type CompanyStaffUserType = (typeof COMPANY_STAFF_USER_TYPES)[number];

export function isCompanyStaffUserType(userType: string | undefined): userType is CompanyStaffUserType {
  return COMPANY_STAFF_USER_TYPES.includes(userType as CompanyStaffUserType);
}
